/**
 * One root-level prompt per app load.
 *
 * Three components can decide to interrupt someone the moment the page settles:
 * the password-recovery modal, the Watchtower launch popup, and the
 * product-update opt-in. They live at the app root, they resolve
 * asynchronously, and nothing used to stop two of them landing on the same
 * load - two stacked `fixed inset-0 z-50` overlays, the newer one painting over
 * a question the user had not answered yet.
 *
 * The slot is claimed by whichever gets there first, EXCEPT that a
 * higher-priority prompt takes it and evicts the holder. Priority is about how
 * much the interruption is worth to the person seeing it:
 *
 *  - password-recovery: they clicked a reset link. It is the reason they are
 *    here, and it must never be buried.
 *  - watchtower: a one-time launch announcement. Losing the slot costs a real
 *    offer, and it only re-asks on a later load.
 *  - email-optin: asked once and never urgent. It yields to everything and
 *    tries again next load.
 *
 * An evicted holder is told to close and MUST NOT record an answer - nothing
 * was answered. That is why eviction is a callback rather than a flag: the
 * holder needs to unwind its own state, not just stop rendering.
 *
 * Module state, so its lifetime is the page load. That is exactly the scope the
 * rule is written in.
 */

export type RootPromptId = 'password-recovery' | 'watchtower' | 'email-optin'

const PRIORITY: Record<RootPromptId, number> = {
  'password-recovery': 3,
  watchtower: 2,
  'email-optin': 1,
}

let holder: { id: RootPromptId; onEvict: () => void } | null = null

/**
 * True when `id` owns the slot and may render. Re-claiming by the current
 * holder is a no-op that refreshes its evict callback, so a component whose
 * effect runs twice (StrictMode) does not lose its own slot.
 */
export function claimRootPrompt(id: RootPromptId, onEvict: () => void): boolean {
  if (holder?.id === id) {
    holder.onEvict = onEvict
    return true
  }
  if (holder && PRIORITY[holder.id] >= PRIORITY[id]) return false
  const evicted = holder
  holder = { id, onEvict }
  // After the handover, never before: the evicted holder's close handler can
  // run synchronously, and it must not be able to see itself as the holder.
  evicted?.onEvict()
  return true
}

/** Releasing a slot you do not hold is a no-op, not an error. */
export function releaseRootPrompt(id: RootPromptId): void {
  if (holder?.id === id) holder = null
}

export function rootPromptHolder(): RootPromptId | null {
  return holder?.id ?? null
}

/** Tests only. The real lifetime of this state is the page load. */
export function resetRootPrompts(): void {
  holder = null
}
